<template>
    <div>
        <!-- MOBIL: app-bar s hamburgerem, Day/Steps, road progressem a new-day tlacitkem -->
        <v-app-bar v-if="isMobile" app dark color="rgba(33,33,33,0.92)" height="60">
            <v-badge
                :value="trainerHasAction"
                color="transparent"
                overlap
                offset-x="18"
                offset-y="18"
                :class="{'topbar-mirror-last': rightHanded}"
            >
                <template v-slot:badge>
                    <v-icon size="16" style="color: #FFD700; filter: drop-shadow(0 0 3px rgba(0,0,0,0.9)) drop-shadow(0 0 3px rgba(0,0,0,0.9));">mdi-star</v-icon>
                </template>
                <v-app-bar-nav-icon @click="$emit('toggle-drawer')" style="width: 48px; height: 48px;"/>
            </v-badge>

            <span class="ml-2 text-no-wrap topbar-stat">
                <v-icon size="22">mdi-weather-sunny</v-icon> {{ days }}
            </span>
            <span
                class="ml-3 text-no-wrap pa-1 pr-2 topbar-stat"
                :class="{'red rounded': steps <= 0}"
            >
                <v-icon size="22">mdi-shoe-print</v-icon> {{ steps }}
            </span>

            <div class="flex-grow-1 mx-2">
                <v-progress-linear
                    v-if="showRoadProgress"
                    :value="roadProgress"
                    color="warning"
                    background-color="rgba(255,255,255,0.35)"
                    height="16"
                    rounded
                >
                    <span style="font-size: 11px;" class="font-weight-bold">{{ $t('layout.topBar.roadLabel') }}</span>
                </v-progress-linear>
            </div>

            <div
                v-if="showNewDayWidget"
                class="text-center"
                :class="{'deactivated-stepper': isNewDayBlocked, 'new-day-pulse': steps <= 0 && !isNewDayBlocked, 'topbar-mirror-first': rightHanded}"
            >
                <v-progress-circular
                    :value="percentage"
                    :color="ringColor"
                    :rotate="-90"
                    :size="52"
                    :width="40"
                    @click="newDay"
                >
                    <v-avatar
                        width="42"
                        height="42"
                        class="black--text font-weight-bold btn-shadow"
                        :class="stepAvatarClass"
                    >
                        <v-icon v-if="!isLoading" size="32" class="black--text">mdi-shoe-print</v-icon>
                        <v-btn v-else loading fab width="42" height="42" class="black--text" :class="stepAvatarClass"/>
                    </v-avatar>
                </v-progress-circular>
            </div>
        </v-app-bar>

        <!-- DESKTOP: plovouci bloky pres herni art (vizualne jako stary Stepper, posunute o drawer rail) -->
        <template v-else>
            <div
                v-if="showNewDayWidget"
                class="ma-2"
                style="position:fixed; top: 20px; left: 60px; z-index: 1000;"
            >
                <v-card-text class="pa-0 ma-0 pb-2">
                    <div
                        v-if="steps <= 0 && !isNewDayBlocked"
                        style="position: absolute; right: -10px; top: 13px; width: 0; border-top: 13px solid transparent; border-bottom: 13px solid transparent; border-right: 10px solid rgba(0, 0, 0, 0.6) !important;"
                    >
                    </div>
                    <div
                        v-if="steps <= 0 && !isNewDayBlocked"
                        class="transparent06--dark pic-bg-obstacle pa-1 pl-2 pr-2"
                        style="pointer-events: none; position: absolute; max-width:80px; min-width:55px; right: -90px; z-index:1000; border-radius: 5px;"
                    >
                        {{ $t('layout.topBar.newDayHint') }}
                    </div>
                    <v-card-text class="pa-0 ma-1">
                        <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                                <div
                                    class="text-center pa-0 ma-0"
                                    style="z-index: 1000"
                                    v-bind="attrs"
                                    v-on="on"
                                    :class="{'deactivated-stepper': isNewDayBlocked}"
                                >
                                    <v-progress-circular
                                        :value="percentage"
                                        :color="ringColor"
                                        class="text-center pa-0 ma-0"
                                        :rotate="-90"
                                        :size="64"
                                        :width="50"
                                        @click="newDay"
                                    >
                                        <v-avatar
                                            width="50"
                                            height="50"
                                            class="black--text font-weight-bold btn-shadow"
                                            :class="stepAvatarClass"
                                        >
                                            <v-icon v-if="!isLoading" size="40" class="black--text ml-0 pl-0">mdi-shoe-print</v-icon>
                                            <v-btn v-else loading fab width="50" height="50" class="black--text" :class="stepAvatarClass"/>
                                        </v-avatar>
                                    </v-progress-circular>
                                </div>
                            </template>
                            <span>{{ isNewDayBlocked ? $t('layout.topBar.newDayTooltipBlocked') : $t('layout.topBar.newDayTooltipReady') }}</span>
                        </v-tooltip>
                    </v-card-text>
                </v-card-text>
            </div>

            <div
                style="position: fixed; width: 140px; left: 0px; top: 0px; z-index: 1001; min-height: 20px;"
                class="transparent06--dark"
            >
                <span class="ma-2 pa-2">
                    <v-icon color="white">mdi-weather-sunny</v-icon> {{ $t('layout.topBar.dayLabel') }} {{ days }}
                </span>
            </div>
            <div
                style="position: fixed; width: 130px; left: 140px; top: 0px; z-index: 1001; min-height: 20px; border-radius: 0px 0px 8px 0px"
                class="transparent06--dark"
                :class="{'red': steps <= 0}"
            >
                <span class="ma-2 pa-2">
                    <span><v-icon color="white">mdi-shoe-print</v-icon> {{ $t('layout.topBar.stepsLabel') }} {{ steps }}</span>
                </span>
            </div>
        </template>
        <ConfirmDialog ref="confirmDialog"/>
    </div>
</template>

<script>
    import {mapActions, mapGetters} from 'vuex';
    import {API_ENTRYPOINT} from '@/config/entrypoint';
    import {canNewDay, cantNewDay, stepperVisible} from '@/utils/newDayRules';
    import {isMobileMixin} from '@/utils/isMobileMixin';
    import {flags} from '@/config/featureFlags';
    import ConfirmDialog from '@/components/utils/ConfirmDialog';

    export default {
        name: 'TopBar',
        components: {ConfirmDialog},
        mixins: [isMobileMixin],
        props: {
            positionId: {type: Number, default: null},
        },
        watch: {
            positionId() {
                this.getDays();
            },
        },
        computed: {
            ...mapGetters({
                user: 'user/item',
                position: 'journey/position',
                steps: 'journey/steps',
                days: 'journey/days',
                maxSteps: 'journey/maxSteps',
                isLoading: 'journey/isLoading',
                trainerHasAction: 'journey/trainerHasAction',
            }),
            percentage() {
                return (100 / this.maxSteps) * this.steps;
            },
            showNewDayWidget() {
                return stepperVisible(this.position, this.$route.name);
            },
            rightHanded() {
                return flags.rightHanded;
            },
            // Krome WILD i FIGHT (divoky souboj s pokemonem) a FIGHT_TRAINER, ale jen divoky trener (!stadionFight) - u gym leadera uz jsi ve meste/stadionu, cesta tam nema smysl.
            showRoadProgress() {
                if (!this.position || this.$route.name !== 'Journey') return false;
                if (!this.position.city || this.position.city.level >= 9) return false;
                if (this.position.type === 'WILD' || this.position.type === 'FIGHT') return true;
                if (this.position.type === 'FIGHT_TRAINER') {
                    return !!this.position.trainerFight && !this.position.trainerFight.stadionFight;
                }
                return false;
            },
            roadProgress() {
                return (parseInt(this.position.roadLenght) / parseInt(this.position.city.roadLenght)) * 100;
            },
            isNewDayBlocked() {
                return cantNewDay(this.position);
            },
            ringColor() {
                return this.isNewDayBlocked ? 'blue-grey lighten-2' : 'secondary';
            },
            stepAvatarClass() {
                return {
                    'primary': this.steps > 3 && !this.isNewDayBlocked,
                    'warning': this.steps <= 3 && this.steps > 0 && !this.isNewDayBlocked,
                    'error': this.steps <= 0 && !this.isNewDayBlocked,
                    'grey': this.isNewDayBlocked,
                };
            },
        },
        created() {
            this.getDays();
        },
        methods: {
            ...mapActions({
                positionPost: 'journey/positionPost',
                refreshActiveTeam: 'pokemon/refreshActiveTeam',
            }),
            async newDay() {
                if (!canNewDay(this.position)) {
                    return;
                }
                // Potvrzeni jen kdyz jeste zbyvaji kroky (steps <= 0 = beztak neni co ztratit) - stejny ConfirmDialog vzor jako Leave/Evaluate (DungeonButtons.vue), respektuje autoConfirm z Options.
                if (this.steps > 0) {
                    const rlyNewDay = await this.$refs.confirmDialog.open(
                        this.$t('layout.topBar.confirmNewDayWithSteps'),
                        {steps: this.steps}
                    );
                    if (!rlyNewDay) {
                        return;
                    }
                }
                const url = new URL('api/new-day/' + this.user.position.id, API_ENTRYPOINT);
                // Novy den leci pokemony (PositionService::newDay -> fullHeal), ale TeamBottomSheet.vue cte HP z vlastniho nezavisleho fetch (pokemon/items) - bez explicitniho refreshe by dal ukazovala stare (zranene) hodnoty.
                this.positionPost({url: url, obj: null, method: 'GET'}).then(() => {
                    this.refreshActiveTeam();
                });
            },
            getDays() {
                const url = new URL('api/get-position/' + this.positionId, API_ENTRYPOINT);
                this.positionPost({url: url, obj: null, method: 'GET'});
            },
        },
    }
</script>

<style scoped>
.new-day-pulse {
    animation: new-day-pulse 1.2s ease-in-out infinite;
}
@keyframes new-day-pulse {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.15); }
}
.topbar-stat {
    font-size: 17px;
    font-weight: 500;
}
/* Zrcadleni pro praváky - vychozi (flag off) je hamburger vlevo/New day vpravo, daleko od palce leváka. `order` na v-app-bar flex kontejneru, Day/Steps/road progress zustavaji uprostred beze zmeny poradi. */
.topbar-mirror-first {
    order: -1;
}
.topbar-mirror-last {
    order: 10;
}
</style>
